import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { usePeriod } from '@/lib/period';
import { PeriodControl } from '@/features/shared/PeriodControl';
import { formatInt, formatUsdCents } from '@/features/dashboard-finance/formatters';
import { ChurnTable } from './ChurnTable';
import { useChurnedTenants } from './useChurnedTenants';

function Kpi({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <Container>
      <Box variant="awsui-key-label">{label}</Box>
      <Box variant="h1">{value}</Box>
    </Container>
  );
}

/**
 * Tela `/dashboard/churn`: tenants suspensos no período, com KPIs (total,
 * receita direta perdida em USD eq., motivo mais comum) e a tabela onde o
 * super-admin categoriza o motivo de cada cancelamento.
 *
 * Origem: sem issue — commit b9c02cd45, 2026-05-04 (Sprint 4.10, ver
 * `docs/sprint-4.10-cohorts-funnel-churn.md`).
 *
 * Contrato: sem props; período pelo `usePeriod` (compartilhado com os outros
 * dashboards). Estados LOADING/ERROR/READY vêm de `useChurnedTenants`; o
 * `refetch` vira o `onChanged` da `ChurnTable`, que recarrega depois de
 * gravar um motivo.
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/churn`, só super-admin).
 *
 * Arquitetura: só apresentação; a leitura é do `useChurnedTenants` e a escrita
 * do motivo é da `ChurnTable` (callable `categorizeChurnReason`, aberta pelo
 * `CategorizeReasonDialog`). Spec: `docs/screens/churn.md`.
 */
export function ChurnDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { period, setPeriod } = usePeriod();
  const { state, refetch } = useChurnedTenants(period);

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('churn.subtitle')}>{t('churn.title')}</Header>}
    >
      <SpaceBetween size="l">
        <PeriodControl value={period} onChange={setPeriod} />

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
        {state.kind === 'READY' && (
          <SpaceBetween size="l">
            <ColumnLayout columns={3}>
              <Kpi label={t('churn.summary.totalRows')} value={formatInt(state.summary.totalRows)} />
              <Kpi
                label={t('churn.summary.revenueLost')}
                value={formatUsdCents(state.summary.revenueLostUsdCents)}
              />
              <Kpi
                label={t('churn.summary.topReason')}
                value={
                  state.summary.topReason
                    ? `${t(`churn.reason.${state.summary.topReason}`)} (${formatInt(state.summary.topReasonCount)})`
                    : '—'
                }
              />
            </ColumnLayout>
            <ChurnTable rows={state.rows} onChanged={refetch} />
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}
